import { useState } from "react";
import { http, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";

const OPTIONS = [
  { key: "love", emoji: "😍", label: "Love it" },
  { key: "useful", emoji: "🙂", label: "Useful" },
  { key: "needs", emoji: "😐", label: "Needs improvement" },
  { key: "difficult", emoji: "😕", label: "Difficult to use" },
];

export default function FeedbackDialog({ onClose }) {
  const [rating, setRating] = useState("");
  const [suggestion, setSuggestion] = useState("");
  const [likelihood, setLikelihood] = useState(8);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    if (!rating) { setErr("Please pick an option"); return; }
    setErr("");
    setSaving(true);
    try {
      await http.post("/feedback", { rating, suggestion, usage_likelihood: Number(likelihood) });
      toast.success("Thanks for the feedback!");
      onClose();
    } catch (e) {
      setErr(formatApiError(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-lg" data-testid="feedback-dialog">
        <DialogHeader>
          <DialogTitle className="font-heading">What did you think of PatientFlow?</DialogTitle>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-5 pt-2">
          <div className="grid grid-cols-2 gap-2">
            {OPTIONS.map((o) => (
              <button
                type="button"
                key={o.key}
                onClick={() => setRating(o.key)}
                data-testid={`feedback-option-${o.key}`}
                className={`text-left p-3 rounded-md border transition-colors duration-200 ${
                  rating === o.key ? "border-emerald-600 bg-emerald-50" : "border-zinc-200 hover:bg-zinc-50"
                }`}
              >
                <div className="text-2xl">{o.emoji}</div>
                <div className="text-sm font-medium text-zinc-800 mt-1">{o.label}</div>
              </button>
            ))}
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">What would you like us to add?</Label>
            <Textarea rows={3} value={suggestion} onChange={(e) => setSuggestion(e.target.value)} className="mt-1.5" data-testid="feedback-suggestion" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-wider text-zinc-500 font-semibold">
              How likely are you to use this tool regularly? ({likelihood}/10)
            </Label>
            <input
              type="range" min="1" max="10" value={likelihood}
              onChange={(e) => setLikelihood(e.target.value)}
              className="w-full mt-2 accent-emerald-600"
              data-testid="feedback-likelihood"
            />
          </div>

          {err && <div className="text-sm text-red-600">{err}</div>}

          <DialogFooter className="gap-2">
            <Button type="button" variant="outline" onClick={onClose} data-testid="feedback-cancel">Cancel</Button>
            <Button type="submit" disabled={saving} className="bg-emerald-600 hover:bg-emerald-700" data-testid="feedback-submit">
              {saving ? "Sending..." : "Send feedback"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
